<template>
  <Modal v-model="isVisible" width="lg">
    <div slot="header">
      Confirmar Indicação em Lote
    </div>

    <div v-if="driver" class="warning-box mb-3">
      <strong>Atenção!</strong><br>
      Você está prestes a indicar <strong>{{ tickets.length }}</strong> multa(s) para o motorista <strong>{{ driver.name }}</strong>.
      <br>Verifique se este condutor foi realmente responsável por todas as infrações selecionadas antes de confirmar.
    </div>

    <div v-if="driver" class="driver-info-box mb-3">
      <div class="driver-info-line"><strong>Nome:</strong> {{ driver.name }}</div>
      <div class="driver-info-line"><strong>CPF:</strong> {{ formatCpf(driver.cpf) }}</div>
      <div class="driver-info-line"><strong>Categoria:</strong> {{ driver.category || '-' }}</div>
    </div>

    <div class="tickets-box">
      <h6 class="mb-3 tickets-title">Multas selecionadas ({{ tickets.length }}):</h6>
      <table class="table table-borderless table-border table-align">
      <thead>
        <tr>
          <th style="width: 50px;">
            <input
              type="checkbox"
              :checked="allSelected"
              @change="toggleAll"
              class="cursor-pointer"
            />
          </th>
          <th>Auto de Infração</th>
          <th>Placa</th>
          <th>Data da Infração</th>
          <th>Valor</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="indication in ticketsWithSelection" :key="indication.id">
          <td>
            <input
              type="checkbox"
              v-model="indication.selected"
              class="cursor-pointer"
            />
          </td>
          <td>{{ (indication.ticket && indication.ticket.ait) || indication.ait }}</td>
          <td>{{ (indication.ticket && indication.ticket.plate) || indication.plate }}</td>
          <td>{{ formatDate((indication.ticket && indication.ticket.occurredAt) || indication.occurredAt) }}</td>
          <td>{{ formatCurrency((indication.ticket && indication.ticket.valueCents) || indication.valueCents) }}</td>
        </tr>
      </tbody>
    </table>

      <div class="mt-3">
        <small class="text-muted">
          <strong>Total selecionado:</strong> {{ selectedCount }} multa(s)
        </small>
      </div>
    </div>

    <div slot="footer">
      <button class="btn btn-outline mr-2" @click="cancel">
        Cancelar
      </button>
      <button
        class="btn btn-primary"
        @click="confirm"
        :disabled="selectedCount === 0"
      >
        Confirmar e Processar ({{ selectedCount }})
      </button>
    </div>
  </Modal>
</template>

<script>
import Modal from '@/components/shares/specialTag/Modal'

export default {
  name: 'SingleDriverBatchModal',
  components: {
    Modal
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    driver: {
      type: Object,
      default: () => ({
        name: '',
        cpf: '',
        category: ''
      })
    },
    tickets: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      ticketsWithSelection: []
    }
  },
  computed: {
    isVisible: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    },
    allSelected() {
      return this.ticketsWithSelection.length > 0 &&
             this.ticketsWithSelection.every(t => t.selected)
    },
    selectedCount() {
      return this.ticketsWithSelection.filter(t => t.selected).length
    }
  },
  watch: {
    tickets: {
      immediate: true,
      handler(newTickets) {
        if (newTickets && newTickets.length > 0) {
          this.ticketsWithSelection = newTickets.map(ticket => ({
            ...ticket,
            selected: true
          }))
        }
      }
    }
  },
  methods: {
    formatCpf(cpf) {
      if (!cpf) return ''
      return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    },
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleDateString('pt-BR')
    },
    formatCurrency(value) {
      if (!value) return 'R$ 0,00'
      const valueInReais = value / 100
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(valueInReais)
    },
    cancel() {
      this.isVisible = false
      this.$emit('cancel')
    },
    confirm() {
      const selectedTickets = this.ticketsWithSelection.filter(t => t.selected)
      this.isVisible = false
      this.$emit('confirm', selectedTickets)
    },
    toggleAll() {
      const newValue = !this.allSelected
      this.ticketsWithSelection.forEach(t => {
        t.selected = newValue
      })
    }
  }
}
</script>

<style scoped>
.warning-box {
  background-color: var(--alert-warning-bg);
  border: 1px solid var(--alert-warning-border);
  border-radius: 8px;
  padding: 16px;
  color: var(--alert-warning-text);
}

.tickets-title {
  color: var(--primary-text-color);
  font-weight: 600;
}

.driver-info-box {
  background-color: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 16px;
  color: var(--primary-text-color);
}

.driver-info-line {
  margin-bottom: 4px;
  line-height: 1.4;
}

.driver-info-line:last-child {
  margin-bottom: 0;
}

.tickets-box {
  background-color: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 16px;
}

input[type="checkbox"] {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  width: 18px;
  height: 18px;
  border: 2px solid var(--border-color);
  border-radius: 3px;
  background-color: transparent;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
}

input[type="checkbox"]:checked {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}

input[type="checkbox"]:checked::after {
  content: '';
  position: absolute;
  left: 5px;
  top: 2px;
  width: 4px;
  height: 8px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

input[type="checkbox"]:hover {
  border-color: var(--primary-color);
}

input[type="checkbox"]:focus {
  outline: none;
  box-shadow: 0 0 0 2px var(--primary-color-disabled);
}
</style>
